import type { ReactNode, RefObject } from 'react';
import { useState } from 'react';

import type { TooltipProps } from '~/components/Tooltip';
import { cn } from '~/lib/cn';

import { BarChartBar } from './BarChartBar';
import { HorizontalAxis } from './HorizontalAxis';
import type { BarChartDirection } from './types';
import { arraySum, getAxisLabels } from './utils';
import { VerticalAxis } from './VerticalAxis';

export type BarChartDatum = {
  percentageValues: number[];
  categoryIcon?: ReactNode;
  categoryLabel: string;
  valueLabel: string;
  delta?: number;
  tooltip?: TooltipProps;
};

interface BarChartProps {
  data: BarChartDatum[];
  direction?: BarChartDirection;
  className?: string;
}

export const BarChart = ({
  data,
  direction = 'vertical',
  className,
}: BarChartProps) => {
  const [barActive, setBarActive] = useState<
    RefObject<HTMLDivElement> | undefined
  >();
  const isVertical = direction === 'vertical';
  const isHorizontal = direction === 'horizontal';

  const maxValue = Math.max(
    ...data.map((datum) => arraySum(datum.percentageValues)),
  );

  const axisLabels = getAxisLabels(maxValue);

  return (
    <div
      className={cn(
        'relative z-0',
        {
          'flex h-full': isVertical,
          'mb-lg grid grow': isHorizontal,
        },
        className,
      )}
    >
      <div
        className={cn('relative', {
          'flex h-full grow pl-xs': isVertical,
          'flex flex-col px-px pb-xs': isHorizontal,
        })}
      >
        {data.map((datum) => (
          <BarChartBar
            key={datum.categoryLabel}
            {...{
              ...datum,
              direction,
              barActive,
              setBarActive,
              axisLabels,
            }}
          />
        ))}
      </div>
      {isVertical ? <VerticalAxis {...{ axisLabels }} /> : null}
      {isHorizontal ? <HorizontalAxis {...{ axisLabels }} /> : null}
    </div>
  );
};

BarChart.displayName = 'BarChart';
